/**
 * Page Info Banner
 *
 * Displays an informational banner at the top of pages with context,
 * related links, and optional walkthrough tour buttons.
 */

import React from 'react';
import { Info, X, PlayCircle } from '@phosphor-icons/react';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { useBannerDismissal } from './hooks/useBannerDismissal';
import { useWalkthrough } from './hooks/useWalkthrough';
import { CrossNavigationLink } from './CrossNavigationLink';
import { BANNER_CONFIGS } from './config/bannerContent';

interface PageInfoBannerProps {
  /** Unique page identifier to load banner config */
  pageId: string;
  /** Optional override for title */
  title?: string;
  /** Optional override for description */
  description?: string;
  /** Optional override for tour ID */
  tourId?: string;
  /** Optional override for related links */
  links?: { label: string; href: string }[];
}

/**
 * PageInfoBanner Component
 *
 * Shows contextual information about the current page with optional
 * walkthrough tour and navigation links to related pages.
 *
 * Banner can be dismissed by the user and the state persists in localStorage.
 *
 * @example
 * ```tsx
 * // Use with pre-configured banner
 * <PageInfoBanner pageId="dd-settings" />
 *
 * // Override specific properties
 * <PageInfoBanner
 *   pageId="dd-settings"
 *   title="Custom Title"
 *   description="Custom description text"
 * />
 * ```
 */
export const PageInfoBanner: React.FC<PageInfoBannerProps> = ({
  pageId,
  title: titleOverride,
  description: descriptionOverride,
  tourId: tourIdOverride,
  links: linksOverride,
}) => {
  const { isDismissed, dismiss } = useBannerDismissal(pageId);
  const { startTour } = useWalkthrough();

  // Get banner config from predefined configs
  const bannerConfig = BANNER_CONFIGS[pageId];

  // Use overrides or config values
  const title = titleOverride || bannerConfig?.title || '';
  const description = descriptionOverride || bannerConfig?.description || '';
  const tourId = tourIdOverride || bannerConfig?.tourId;
  const links = linksOverride || bannerConfig?.links || [];

  // Don't render if dismissed
  if (isDismissed) {
    return null;
  }

  // Don't render if no content
  if (!title && !description) {
    return null;
  }

  const handleStartTour = () => {
    if (tourId) {
      startTour(tourId);
    }
  };

  return (
    <Alert className="relative mb-4 border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/30">
      {/* Info Icon */}
      <Info size={20} className="text-blue-600 dark:text-blue-400" />

      {/* Content */}
      <div className="flex flex-col gap-3">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            {title && <AlertTitle className="text-blue-900 dark:text-blue-100">{title}</AlertTitle>}
            {description && (
              <AlertDescription className="mt-2 text-blue-800 dark:text-blue-200">
                {description}
              </AlertDescription>
            )}
          </div>

          {/* Close Button */}
          <button
            onClick={dismiss}
            className="flex-shrink-0 rounded-sm opacity-70 hover:opacity-100 transition-opacity"
            aria-label="Banner sluiten"
          >
            <X size={18} className="text-blue-600 dark:text-blue-400" />
          </button>
        </div>

        {/* Actions Row */}
        {(tourId || links.length > 0) && (
          <div className="flex flex-wrap items-center gap-3">
            {/* Start Tour Button */}
            {tourId && (
              <Button
                onClick={handleStartTour}
                variant="outline"
                size="sm"
                className="border-blue-300 text-blue-700 hover:bg-blue-100 dark:border-blue-700 dark:text-blue-300 dark:hover:bg-blue-900/50"
              >
                <PlayCircle size={16} weight="fill" className="mr-2" />
                Start rondleiding
              </Button>
            )}

            {/* Related Links */}
            {links.length > 0 && (
              <div className="flex flex-wrap items-center gap-3">
                {links.map((link, index) => (
                  <React.Fragment key={link.href}>
                    {index > 0 && <span className="text-blue-400 dark:text-blue-600">•</span>}
                    <CrossNavigationLink label={link.label} href={link.href} />
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </Alert>
  );
};
